import React, { useCallback, useContext, useState } from 'react';
import type {
  AppDetails,
  SelfHostedModalContext,
  SelfHostedModalState,
} from './types';
import { useStepper } from '../../../../hooks/useStepper';
import { useInitialStepsLabels } from './consts';
import { useTemplatesData } from '../../../../hooks/useTemplatesData';
import type { DeploymentHostType } from '@wix/ambassador-devcenter-apps-v1-app-template/types';

const initialState = {
  selectedDeploymentProvider: null,
  appName: '',
  baseUrl: '',
  appDetails: undefined,
};

const SelfHostedContext = React.createContext<SelfHostedModalContext>({
  formData: initialState,
  updateSelectedDeploymentProvider: () => {},
  updateAppName: () => {},
  updateBaseUrl: () => {},
  activeStep: 0,
  steps: [],
  nextStep: () => {},
  onClose: () => {},
  updateAppDetails: () => {},
  previousStep: () => {},
});

export const SelfHostedModalContextProvider = ({ children, onClose }) => {
  const [formData, setFormData] = useState<SelfHostedModalState>(initialState);
  const initialSteps = useInitialStepsLabels();
  const { steps, activeStep, onNextStep, onPrevStep } =
    useStepper(initialSteps);
  const { currentTemplate: template } = useTemplatesData();

  const updateSelectedDeploymentProvider = useCallback(
    (selectedDeploymentProvider: DeploymentHostType) =>
      setFormData((prev) => ({
        ...prev,
        selectedDeploymentProvider,
        appName: `Copy of ${template?.title || ''}`,
      })),
    [template?.title],
  );
  const updateAppName = (value: string) =>
    setFormData((prev) => ({ ...prev, appName: value }));

  const updateBaseUrl = (value: string) =>
    setFormData((prev) => ({ ...prev, baseUrl: value }));

  const updateAppDetails = (value: AppDetails) =>
    setFormData((prev) => ({ ...prev, appDetails: value }));

  return (
    <SelfHostedContext.Provider
      value={{
        formData,
        updateSelectedDeploymentProvider,
        updateBaseUrl,
        updateAppName,
        updateAppDetails,
        steps,
        activeStep,
        nextStep: onNextStep,
        onClose,
        previousStep: onPrevStep,
      }}
    >
      {children}
    </SelfHostedContext.Provider>
  );
};

export const useSelfHostedContext = () => useContext(SelfHostedContext);
